import { useCallback, useMemo, useState } from 'react';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { Validators } from 'utils';
import { BusinessInfoProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  getAbnValidation,
  businessInfoInitialValues,
  ERROR_MESSAGES,
  ABN_ERROR_MAP,
} from './Abn/useAbnUtils';

type UseBusinessInfoFormikParams = {
  businessInfo: Partial<BusinessInfoProps> | undefined;
  onSubmitBusinessInfo: (values: BusinessInfoProps) => void | Promise<void>;
};

function validateValues(
  values: BusinessInfoProps
): FormikErrors<BusinessInfoProps> {
  const errors: FormikErrors<BusinessInfoProps> = {};
  const abn = values.abn?.trim();

  if (!abn) {
    errors.abn = ERROR_MESSAGES.ABN_REQUIRED;
  } else {
    const { error } = getAbnValidation(abn);
    if (error) {
      errors.abn = ABN_ERROR_MAP[error];
    }
  }

  if (!values.entityName?.trim()) {
    errors.entityName = 'Please enter entity name';
  }

  if (!values.tradingName?.trim()) {
    errors.tradingName = 'Please enter trading name';
  }

  if (!values.industryType?.trim()) {
    errors.industryType = 'Please enter industry type';
  }

  return errors;
}

export const useBusinessInfoFormik = ({
  businessInfo,
  onSubmitBusinessInfo,
}: UseBusinessInfoFormikParams) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const initialValues = useMemo(
    () => ({
      ...businessInfoInitialValues,
      ...(businessInfo ?? {}),
      abn: businessInfo?.abn ? Validators.formatAbn(businessInfo.abn) : '',
    }),
    [businessInfo]
  );

  const formik = useFormik<BusinessInfoProps>({
    initialValues,
    enableReinitialize: true,
    validate: validateValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await onSubmitBusinessInfo(values);
    },
  });

  const { isValid: isAbnValid } = useMemo(
    () => getAbnValidation(formik.values.abn),
    [formik.values.abn]
  );

  const validateAbnField = useCallback(
    (
      value: string,
      mode: 'change' | 'blur',
      onInvalid?: () => void,
      onClearExternalError?: () => void
    ): { isValid: boolean; cleanAbn: string } => {
      const { cleanAbn, isValid } = getAbnValidation(value);
      const hasExactLength = cleanAbn.length === 11;

      if (mode === 'change') {
        if (cleanAbn.length === 0 || cleanAbn.length < 11) {
          formik.setFieldError('abn', undefined);
          onClearExternalError?.();
          return { isValid: false, cleanAbn };
        }

        if (!hasExactLength) {
          formik.setFieldError('abn', ERROR_MESSAGES.ABN_INVALID_LENGTH);
          formik.setFieldTouched('abn', true, false);
          onClearExternalError?.();
          onInvalid?.();
          return { isValid: false, cleanAbn };
        }

        if (!isValid) {
          formik.setFieldError('abn', ERROR_MESSAGES.ABN_INVALID);
          formik.setFieldTouched('abn', true, false);
          onClearExternalError?.();
          onInvalid?.();
          return { isValid: false, cleanAbn };
        }

        formik.setFieldError('abn', undefined);
        onClearExternalError?.();
        return { isValid: true, cleanAbn };
      }

      if (!hasExactLength) {
        formik.setFieldError('abn', ERROR_MESSAGES.ABN_INVALID_LENGTH);
        formik.setFieldTouched('abn', true, false);
        onClearExternalError?.();
        onInvalid?.();
        return { isValid: false, cleanAbn };
      }

      if (!isValid) {
        formik.setFieldError('abn', ERROR_MESSAGES.ABN_INVALID);
        formik.setFieldTouched('abn', true, false);
        onClearExternalError?.();
        onInvalid?.();
        return { isValid: false, cleanAbn };
      }

      formik.setFieldError('abn', undefined);
      onClearExternalError?.();
      return { isValid: true, cleanAbn };
    },
    [formik]
  );

  const markAllTouched = useCallback(() => {
    const touched = Object.keys(formik.values).reduce((acc, key) => {
      acc[key as keyof BusinessInfoProps] = true;
      return acc;
    }, {} as FormikTouched<BusinessInfoProps>);

    formik.setTouched(touched, false);
  }, [formik]);

  return {
    formik,
    initialValues,
    isAbnValid,
    submitAttempted,
    setSubmitAttempted,
    validateAbnField,
    markAllTouched,
  };
};
